/**
 * Utility functions for scroll behavior with accessibility support
 */

/**
 * Smoothly scrolls to an element with accessibility support
 * Respects user's prefers-reduced-motion preference
 *
 * @param element - The target element to scroll to
 * @param useRAF - Whether to use requestAnimationFrame (useful for ensuring DOM is painted)
 */
export const smoothScrollToElement = (
  element: HTMLElement,
  useRAF: boolean = false
): void => {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const scroll = () => {
    element.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  if (useRAF) {
    requestAnimationFrame(scroll);
  } else {
    scroll();
  }
};

/**
 * Executes a function when the browser is idle
 * Falls back to requestAnimationFrame + setTimeout if requestIdleCallback is not available
 *
 * @param fn - The function to execute when idle
 * @param timeout - Optional timeout in milliseconds (default: 3000)
 */
export const runWhenIdle = (fn: () => void, timeout: number = 3000): void => {
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(fn, {timeout});
  } else {
    requestAnimationFrame(() => setTimeout(fn, 0));
  }
};
